import React, { useEffect, useMemo, useState } from "react";
import { Modal, Select, Checkbox, Button, Table, Alert, Popconfirm, message } from "antd";
import { useCopyCompanySettings } from "../hooks/useFetch";

// ต้องตรงกับ SECTIONS ใน BackEnd/src/api/v1/companies/copySettingsModel.js
const SECTION_OPTIONS = [
  { value: "caseSetting", label: "ตั้งค่าเคส (เคสหลัก + เคสย่อย + Custom field + การผูกฟิลด์)" },
  { value: "blacklistTopic", label: "หัวข้อ Blacklist" },
  { value: "errorTopic", label: "หัวข้อเคสผิดพลาด" },
  { value: "notification", label: "การแจ้งเตือน (เสียง/เวลา)" },
  { value: "caseTime", label: "เวลาเคส ฝาก/ถอน/อื่นๆ" },
  { value: "lineRemark", label: "ตัวเลือกหมายเหตุ LINE" },
];
const ALL_SECTIONS = SECTION_OPTIONS.map((o) => o.value);

// แปลงผลจาก backend เป็นแถวในตาราง (ตั้งค่าเคสแตกเป็น 4 แถว)
const toRows = (sections) => {
  const rows = [];
  const label = (v) => SECTION_OPTIONS.find((o) => o.value === v)?.label || v;
  Object.entries(sections || {}).forEach(([key, s]) => {
    if (key === "caseSetting") {
      rows.push({ key: "cases", name: "เคสหลัก", add: s.cases.add, skip: s.cases.skip });
      rows.push({
        key: "subcases", name: "เคสย่อย", add: s.subcases.add, skip: s.subcases.skip,
        note: s.subcases.orphan ? `ไม่คัดลอก ${s.subcases.orphan} รายการ (เคสหลักถูกลบไปแล้วในต้นทาง)` : "",
      });
      rows.push({ key: "customfields", name: "Custom field", add: s.customfields.add, skip: s.customfields.skip });
      rows.push({
        key: "links", name: "การผูกฟิลด์กับเคสย่อย", add: s.links.add, skip: s.links.skip,
        note: s.links.foreign ? `ไม่คัดลอก ${s.links.foreign} รายการ (ฟิลด์เป็นของบริษัทอื่น)` : "",
      });
      return;
    }
    rows.push({
      key, name: label(key).replace(/ \(.*\)$/, ""), add: s.add, skip: s.skip,
      note: key === "caseTime" && s.skip ? "ช่องที่ตั้งไว้แล้วจะไม่ถูกทับ" : "",
    });
  });
  return rows;
};

const columns = [
  { title: "หมวด", dataIndex: "name" },
  { title: "จะเพิ่ม", dataIndex: "add", align: "right", render: (v) => <b>{v}</b> },
  { title: "ข้าม (มีชื่อนี้แล้ว)", dataIndex: "skip", align: "right" },
  { title: "หมายเหตุ", dataIndex: "note", render: (v) => <span style={{ color: "#8c8c8c" }}>{v}</span> },
];

/**
 * คัดลอกการตั้งค่าจากบริษัทอื่นเข้าบริษัท target
 * ขั้นตอน: เลือกต้นทาง + หมวด → "ตรวจจำนวน" (ยังไม่เขียน) → "ยืนยันคัดลอก"
 * เพิ่มเฉพาะชื่อที่ปลายทางยังไม่มี ไม่ลบ/ไม่แก้ของเดิม
 */
const CopySettingsModal = ({ open, target, companies = [], onClose }) => {
  const [sourceId, setSourceId] = useState(null);
  const [sections, setSections] = useState(ALL_SECTIONS);
  const [preview, setPreview] = useState(null); // ผล dryRun ของค่าที่เลือกอยู่
  const [done, setDone] = useState(null);
  const { mutateAsync: copySettings, isLoading } = useCopyCompanySettings();

  useEffect(() => {
    if (open) {
      setSourceId(null);
      setSections(ALL_SECTIONS);
      setPreview(null);
      setDone(null);
    }
  }, [open, target?.id]);

  // เปลี่ยนต้นทาง/หมวดแล้ว ตัวเลขเดิมใช้ไม่ได้ ต้องตรวจใหม่
  useEffect(() => setPreview(null), [sourceId, sections]);

  const sourceOptions = useMemo(
    () => companies
      .filter((c) => String(c.id) !== String(target?.id))
      .map((c) => ({ value: c.id, label: c.companyName })),
    [companies, target?.id]
  );

  const run = async (dryRun) => {
    try {
      const res = await copySettings({ targetId: target.id, sourceCompanyId: sourceId, sections, dryRun });
      if (dryRun) setPreview(res.result);
      else {
        setDone(res.result);
        message.success("คัดลอกการตั้งค่าสำเร็จ");
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "คัดลอกการตั้งค่าไม่สำเร็จ");
    }
  };

  const totalAdd = preview ? toRows(preview.sections).reduce((n, r) => n + (r.add || 0), 0) : 0;
  const sourceName = sourceOptions.find((o) => o.value === sourceId)?.label;

  return (
    <Modal
      title={`คัดลอกการตั้งค่าเข้า ${target?.companyName || ""}`}
      open={open}
      onCancel={onClose}
      width={720}
      maskClosable={false}
      footer={
        done ? (
          <Button type="primary" onClick={onClose}>ปิด</Button>
        ) : (
          <>
            <Button onClick={onClose}>ยกเลิก</Button>
            <Button
              onClick={() => run(true)}
              loading={isLoading && !preview}
              disabled={!sourceId || !sections.length || isLoading}
            >
              ตรวจจำนวน
            </Button>
            <Popconfirm
              title="ยืนยันคัดลอก?"
              description={`เพิ่ม ${totalAdd} รายการจาก ${sourceName || ""} เข้า ${target?.companyName || ""} — ย้อนกลับเองไม่ได้`}
              okText="คัดลอก"
              cancelText="ยกเลิก"
              onConfirm={() => run(false)}
              disabled={!preview || totalAdd === 0}
            >
              <Button type="primary" disabled={!preview || totalAdd === 0 || isLoading} loading={isLoading && !!preview}>
                ยืนยันคัดลอก
              </Button>
            </Popconfirm>
          </>
        )
      }
    >
      {done ? (
        <>
          <Alert type="success" showIcon style={{ marginBottom: 12 }} message={`คัดลอกจาก ${sourceName} เรียบร้อย`} />
          <Table size="small" pagination={false} columns={columns} dataSource={toRows(done.sections)} />
        </>
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="เพิ่มเฉพาะรายการที่ชื่อยังไม่มีในบริษัทนี้ ไม่ลบหรือแก้ของเดิม"
            description="ไม่คัดลอก: System Job, Slip Provider, Telegram, LINE channel, ข้อมูล VIP, พนักงานและตารางงาน (เป็นของเฉพาะแต่ละบริษัท)"
          />
          <div style={{ fontWeight: 600, marginBottom: 6 }}>คัดลอกจากบริษัท</div>
          <Select
            style={{ width: "100%", marginBottom: 16 }}
            placeholder="เลือกบริษัทต้นทาง"
            options={sourceOptions}
            value={sourceId}
            onChange={setSourceId}
          />
          <div style={{ fontWeight: 600, marginBottom: 6 }}>หมวดที่จะคัดลอก</div>
          <Checkbox.Group
            style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}
            options={SECTION_OPTIONS}
            value={sections}
            onChange={setSections}
          />
          {preview && (
            <>
              <Table size="small" pagination={false} columns={columns} dataSource={toRows(preview.sections)} />
              {totalAdd === 0 && (
                <Alert type="warning" showIcon style={{ marginTop: 12 }} message="ไม่มีอะไรต้องเพิ่ม — บริษัทนี้มีครบทุกรายการแล้ว" />
              )}
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default CopySettingsModal;
